/**
 * 管理端路由表：对齐原型 `prototype/admin/src/App.tsx:31-52`。
 *
 * 21 条业务路由（=21 个菜单叶子）覆盖 29 个 ADM 编号（含合并视图 ADM-02/04 等）；
 * 登录页 + RequireAuth 守卫；ADM-04/12/13 不建独立路由（以合并 pageCode 表达），
 * ADM-30 不建；`path="*"` 重定向首页（不返回 404）。
 * 全部业务路由经 RequireMenuPermission 按菜单键守卫（rbac-menu-key-alignment:SC-09/SC-10，
 * 菜单隐藏的页面 URL 直达返回无权限页；后端 @Roles 仍是最终防线）。
 * 覆盖 admin-web-shell:SC-07/SC-08。
 */
import { type ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AdminLayout } from "../layout/AdminLayout";
import { RequireAuth } from "../auth/RequireAuth";
import { LoginPage } from "../pages/LoginPage";
import { PlaceholderPage } from "../components/PlaceholderPage";
import { allLeaves } from "../layout/menuConfig";
import { CampusesPage } from "../pages/org/CampusesPage";
import { StaffPage } from "../pages/org/StaffPage";
import { RolePage } from "../pages/org/RolePage";
import { SubjectsPage } from "../pages/catalog/SubjectsPage";
import { CourseSkusPage } from "../pages/catalog/CourseSkusPage";
import { ProductsPage } from "../pages/catalog/ProductsPage";
import { ClassroomsPage } from "../pages/catalog/ClassroomsPage";
import { StudentEnrollmentCompositePage as StudentListPage } from "../pages/students/StudentListPage";
import { OrderListPage } from "../pages/billing/OrderListPage";
import { FundExceptionPage } from "../pages/billing/FundExceptionPage";
import { ClassListPage } from "../pages/classes/ClassListPage";
import { PlacementWorkbenchPage } from "../pages/classes/PlacementWorkbenchPage";
import { SettingsPage } from "../pages/system/SettingsPage";
import { OperationLogPage } from "../pages/operations/OperationLogPage";
import { ScheduleWorkbenchPage } from "../pages/classes/ScheduleWorkbenchPage";
import { DeductionRecordPage } from "../pages/deduction/DeductionRecordPage";
import { MakeupDeductionPage } from "../pages/deduction/MakeupDeductionPage";
import { RequireMenuPermission } from "../auth/RequireMenuPermission";
import { RevenuePage } from "../pages/billing/RevenuePage";
import { DeductionExceptionPage } from "../pages/deduction/DeductionExceptionPage";
import { LedgerPage } from "../pages/deduction/LedgerPage";
import { BalanceInquiryPage } from "../pages/deduction/BalanceInquiryPage";
import { OperationsDashboardPage } from "../pages/operations/OperationsDashboardPage";

/** 已实现为独立组件的页面路径集合（从占位路由中排除，避免重复注册）。 */
const implementedPaths = new Set<string>([
  "/",
  "/org/campuses",
  "/org/staff",
  "/org/roles",
  "/catalog/subjects",
  "/catalog/skus",
  "/catalog/products",
  "/catalog/classrooms",
  "/students",
  "/billing/orders",
  "/billing/fund-exceptions",
  "/classes",
  "/classes/placement",
  "/system/settings",
  "/system/logs",
  "/classes/schedule",
  "/deduction/records",
  "/deduction/makeup",
  "/billing/revenue",
  "/deduction/exceptions",
  "/deduction/ledger",
  "/deduction/balances",
]);

/** 路径 -> 菜单叶子（路由守卫与左侧菜单可见性同源，新增页面必须先登记 menuConfig）。 */
const leafByPath = new Map(allLeaves.map((leaf) => [leaf.path, leaf]));

/** 用菜单叶子权限守卫包裹页面元素（菜单未登记的路径 fail-closed 一律拒绝）。 */
function guarded(path: string, element: ReactNode): ReactNode {
  return <RequireMenuPermission leaf={leafByPath.get(path)}>{element}</RequireMenuPermission>;
}

export function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        element={
          <RequireAuth>
            <AdminLayout />
          </RequireAuth>
        }
      >
        <Route path="/" element={guarded("/", <OperationsDashboardPage />)} />
        <Route path="/org/campuses" element={guarded("/org/campuses", <CampusesPage />)} />
        <Route path="/org/staff" element={guarded("/org/staff", <StaffPage />)} />
        <Route path="/org/roles" element={guarded("/org/roles", <RolePage />)} />
        <Route path="/catalog/subjects" element={guarded("/catalog/subjects", <SubjectsPage />)} />
        <Route path="/catalog/skus" element={guarded("/catalog/skus", <CourseSkusPage />)} />
        <Route path="/catalog/products" element={guarded("/catalog/products", <ProductsPage />)} />
        <Route
          path="/catalog/classrooms"
          element={guarded("/catalog/classrooms", <ClassroomsPage />)}
        />
        <Route path="/students" element={guarded("/students", <StudentListPage />)} />
        <Route path="/billing/orders" element={guarded("/billing/orders", <OrderListPage />)} />
        <Route
          path="/billing/fund-exceptions"
          element={guarded("/billing/fund-exceptions", <FundExceptionPage />)}
        />
        <Route path="/classes" element={guarded("/classes", <ClassListPage />)} />
        <Route
          path="/classes/placement"
          element={guarded("/classes/placement", <PlacementWorkbenchPage />)}
        />
        <Route path="/system/settings" element={guarded("/system/settings", <SettingsPage />)} />
        <Route path="/system/logs" element={guarded("/system/logs", <OperationLogPage />)} />
        <Route
          path="/classes/schedule"
          element={guarded("/classes/schedule", <ScheduleWorkbenchPage />)}
        />
        <Route
          path="/deduction/records"
          element={guarded("/deduction/records", <DeductionRecordPage />)}
        />
        <Route
          path="/deduction/makeup"
          element={guarded("/deduction/makeup", <MakeupDeductionPage />)}
        />
        <Route path="/billing/revenue" element={guarded("/billing/revenue", <RevenuePage />)} />
        <Route
          path="/deduction/exceptions"
          element={guarded("/deduction/exceptions", <DeductionExceptionPage />)}
        />
        <Route path="/deduction/ledger" element={guarded("/deduction/ledger", <LedgerPage />)} />
        <Route path="/deduction/balances" element={guarded("/deduction/balances", <BalanceInquiryPage />)} />
        {allLeaves
          .filter((leaf) => !implementedPaths.has(leaf.path))
          .map((leaf) => (
            <Route
              key={leaf.key}
              path={leaf.path}
              element={
                <RequireMenuPermission leaf={leaf}>
                  <PlaceholderPage leaf={leaf} />
                </RequireMenuPermission>
              }
            />
          ))}
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
